import { useEffect, useState } from 'react';
import { Plus, Package, MoreHorizontal, Pencil, Eye, ArrowRightLeft, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  ConfirmDialog,
  BulkUpload,
  BulkUploadColumn,
} from '@/components/common';
import { inventoryApi, InventoryItem, CreateInventoryItemDto, QueryInventoryParams, InventoryDashboard, locationsApi, Location, RestockDto, TransferDto } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { Separator } from '@/components/ui/separator';

const statusMap: Record<string, 'optimal' | 'low' | 'stockout' | 'overstock'> = {
  Optimal: 'optimal',
  Low: 'low',
  Stockout: 'stockout',
  Overstock: 'overstock',
};

const categories = ['Drilling', 'Safety', 'Tools', 'Parts', 'Equipment', 'Consumables', 'Chemicals'];
const units = ['EA', 'FT', 'M', 'KG', 'LB', 'GAL', 'L', 'BOX', 'SET'];

interface InventoryFormData {
  sku: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  unit: string;
  minStockLevel: string;
  maxStockLevel: string;
  reorderPoint: string;
  leadTimeDays: string;
  unitCost: string;
  isActive: boolean;
}

const emptyFormData: InventoryFormData = {
  sku: '',
  name: '',
  description: '',
  category: 'Parts',
  subcategory: '',
  unit: 'EA',
  minStockLevel: '10',
  maxStockLevel: '',
  reorderPoint: '',
  leadTimeDays: '',
  unitCost: '',
  isActive: true,
};

export default function Inventory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<InventoryDashboard | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item?: InventoryItem }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [formData, setFormData] = useState<InventoryFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  // Restock modal state
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockItem, setRestockItem] = useState<InventoryItem | null>(null);
  const [restockData, setRestockData] = useState({
    sourceLocationId: '',
    destinationLocationId: '',
    quantity: 1,
    unitPrice: '',
    notes: '',
  });
  const [restockSaving, setRestockSaving] = useState(false);

  // Transfer modal state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({
    sourceLocationId: '',
    destinationLocationId: '',
    items: [] as { itemId: string; item?: InventoryItem; quantity: number }[],
    notes: '',
  });
  const [transferSaving, setTransferSaving] = useState(false);

  // Locations for dropdowns
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Bulk upload state
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: QueryInventoryParams = {
        search: searchQuery || undefined,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter as InventoryItem['status'];
      }
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }

      const [itemsResponse, dashboardResponse] = await Promise.all([
        inventoryApi.getAll(params),
        inventoryApi.getDashboard(),
      ]);

      setItems(itemsResponse.data);
      setDashboard(dashboardResponse);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery, statusFilter, categoryFilter]);

  const handleDelete = async () => {
    if (!deleteDialog.item) return;

    try {
      setDeleting(true);
      await inventoryApi.delete(deleteDialog.item.id);
      toast.success('Item deactivated successfully');
      setDeleteDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const openCreateForm = () => {
    setEditingItem(null);
    setFormData(emptyFormData);
    setFormOpen(true);
  };

  const openEditForm = (item: InventoryItem) => {
    setEditingItem(item);
    setFormData({
      sku: item.sku,
      name: item.name,
      description: item.description || '',
      category: item.category,
      subcategory: item.subcategory || '',
      unit: item.unit,
      minStockLevel: item.minStockLevel?.toString() || '',
      maxStockLevel: item.maxStockLevel?.toString() || '',
      reorderPoint: item.reorderPoint?.toString() || '',
      leadTimeDays: item.leadTimeDays?.toString() || '',
      unitCost: item.unitCost?.toString() || '',
      isActive: item.isActive,
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.sku || !formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload: CreateInventoryItemDto = {
        sku: formData.sku,
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category,
        subcategory: formData.subcategory || undefined,
        unit: formData.unit || 'EA',
        companyId: user?.companyId || '',
        minStockLevel: formData.minStockLevel ? parseInt(formData.minStockLevel) : undefined,
        maxStockLevel: formData.maxStockLevel ? parseInt(formData.maxStockLevel) : undefined,
        reorderPoint: formData.reorderPoint ? parseInt(formData.reorderPoint) : undefined,
        leadTimeDays: formData.leadTimeDays ? parseInt(formData.leadTimeDays) : undefined,
        unitCost: formData.unitCost ? parseFloat(formData.unitCost) : undefined,
        isActive: formData.isActive,
      };

      if (editingItem) {
        await inventoryApi.update(editingItem.id, payload);
        toast.success('Item updated successfully');
      } else {
        await inventoryApi.create(payload);
        toast.success('Item created successfully');
      }

      setFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // Fetch locations for restock/transfer modals
  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await locationsApi.getAll({ isActive: true });
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  };

  // Restock handlers
  const openRestockModal = (item: InventoryItem) => {
    setRestockItem(item);
    setRestockData({
      sourceLocationId: '',
      destinationLocationId: '',
      quantity: 1,
      unitPrice: item.unitCost?.toString() || '',
      notes: '',
    });
    fetchLocations();
    setRestockOpen(true);
  };

  const handleRestock = async () => {
    if (!restockItem) return;
    if (!restockData.sourceLocationId || !restockData.destinationLocationId) {
      toast.error('Please select both source and destination locations');
      return;
    }
    if (restockData.sourceLocationId === restockData.destinationLocationId) {
      toast.error('Source and destination must be different');
      return;
    }
    if (restockData.quantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    try {
      setRestockSaving(true);
      const payload: RestockDto = {
        itemId: restockItem.id,
        sourceLocationId: restockData.sourceLocationId,
        destinationLocationId: restockData.destinationLocationId,
        quantity: restockData.quantity,
        unitPrice: restockData.unitPrice ? parseFloat(restockData.unitPrice) : undefined,
        notes: restockData.notes || undefined,
      };
      await inventoryApi.restock(payload);
      toast.success('Restock request submitted successfully');
      setRestockOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRestockSaving(false);
    }
  };

  // Transfer handlers
  const openTransferModal = () => {
    setTransferData({
      sourceLocationId: '',
      destinationLocationId: '',
      items: [],
      notes: '',
    });
    fetchLocations();
    setTransferOpen(true);
  };

  const handleAddTransferItem = () => {
    setTransferData({
      ...transferData,
      items: [...transferData.items, { itemId: '', quantity: 1 }],
    });
  };

  const handleRemoveTransferItem = (index: number) => {
    setTransferData({
      ...transferData,
      items: transferData.items.filter((_, i) => i !== index),
    });
  };

  const handleTransferItemChange = (index: number, field: 'itemId' | 'quantity', value: string | number) => {
    const newItems = [...transferData.items];
    if (field === 'itemId') {
      const selectedItem = items.find((i) => i.id === value);
      newItems[index] = { ...newItems[index], itemId: value as string, item: selectedItem };
    } else {
      newItems[index] = { ...newItems[index], quantity: value as number };
    }
    setTransferData({ ...transferData, items: newItems });
  };

  const handleTransfer = async () => {
    if (!transferData.sourceLocationId || !transferData.destinationLocationId) {
      toast.error('Please select both source and destination locations');
      return;
    }
    if (transferData.sourceLocationId === transferData.destinationLocationId) {
      toast.error('Source and destination must be different');
      return;
    }
    if (transferData.items.length === 0) {
      toast.error('Please add at least one item to transfer');
      return;
    }
    for (const item of transferData.items) {
      if (!item.itemId || item.quantity <= 0) {
        toast.error('All items must have a valid selection and quantity greater than 0');
        return;
      }
    }

    try {
      setTransferSaving(true);
      const payload: TransferDto = {
        sourceLocationId: transferData.sourceLocationId,
        destinationLocationId: transferData.destinationLocationId,
        items: transferData.items.map((i) => ({
          itemId: i.itemId,
          quantity: i.quantity,
        })),
        notes: transferData.notes || undefined,
      };
      await inventoryApi.transfer(payload);
      toast.success('Transfer request submitted successfully');
      setTransferOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTransferSaving(false);
    }
  };

  // Bulk upload configuration
  const bulkUploadColumns: BulkUploadColumn[] = [
    { key: 'sku', label: 'SKU', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category', required: true, validate: (val) =>
      categories.includes(val) ? null : `Invalid category. Use: ${categories.join(', ')}`
    },
    { key: 'subcategory', label: 'Subcategory' },
    { key: 'unit', label: 'Unit', validate: (val) =>
      !val || units.includes(val.toUpperCase()) ? null : `Invalid unit. Use: ${units.join(', ')}`
    },
    { key: 'minStockLevel', label: 'Min Stock', type: 'number' },
    { key: 'maxStockLevel', label: 'Max Stock', type: 'number' },
    { key: 'reorderPoint', label: 'Reorder Point', type: 'number' },
    { key: 'leadTimeDays', label: 'Lead Time (Days)', type: 'number' },
    { key: 'unitCost', label: 'Unit Cost', type: 'number' },
  ];

  const handleBulkUpload = async (rows: Record<string, string>[]) => {
    let success = 0;
    let failed = 0;

    for (const row of rows) {
      try {
        const payload: CreateInventoryItemDto = {
          sku: row.sku.toUpperCase(),
          name: row.name,
          description: row.description || undefined,
          category: row.category,
          subcategory: row.subcategory || undefined,
          unit: row.unit?.toUpperCase() || 'EA',
          companyId: user?.companyId || '',
          minStockLevel: row.minStockLevel ? parseInt(row.minStockLevel) : undefined,
          maxStockLevel: row.maxStockLevel ? parseInt(row.maxStockLevel) : undefined,
          reorderPoint: row.reorderPoint ? parseInt(row.reorderPoint) : undefined,
          leadTimeDays: row.leadTimeDays ? parseInt(row.leadTimeDays) : undefined,
          unitCost: row.unitCost ? parseFloat(row.unitCost) : undefined,
        };
        await inventoryApi.create(payload);
        success++;
      } catch {
        failed++;
      }
    }

    if (success > 0) {
      toast.success(`Successfully imported ${success} items`);
      fetchData();
    }
    if (failed > 0) {
      toast.error(`Failed to import ${failed} items`);
    }

    return { success, failed };
  };

  const columns: Column<InventoryItem>[] = [
    {
      key: 'name',
      header: 'Item',
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Package className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{item.name}</div>
            <div className="text-sm text-muted-foreground">{item.sku}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (item) => (
        <Badge variant="secondary">{item.category}</Badge>
      ),
    },
    {
      key: 'totalQuantity',
      header: 'Total Stock',
      render: (item) => (
        <div>
          <div className="font-medium">{item.aggregated?.totalQuantity || 0} {item.unit}</div>
          <div className="text-sm text-muted-foreground">
            {item.aggregated?.locationCount || 0} locations
          </div>
        </div>
      ),
    },
    {
      key: 'stockLevels',
      header: 'Min / Max',
      render: (item) => (
        <span className="text-muted-foreground font-mono text-sm">
          {item.minStockLevel} / {item.maxStockLevel || '∞'}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      render: (item) => (
        <span className="font-medium">
          ${((item.aggregated?.totalQuantity || 0) * (item.unitCost || 0)).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusBadge status={statusMap[item.status || 'Optimal'] || 'optimal'} />
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/inventory/${item.id}`)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openEditForm(item)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openRestockModal(item)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Restock
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, item })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Deactivate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Inventory"
        description="Manage inventory items and stock levels across locations"
        breadcrumbs={[{ label: 'Inventory' }]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBulkUploadOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Bulk Upload
            </Button>
            <Button variant="outline" onClick={openTransferModal}>
              <ArrowRightLeft className="mr-2 h-4 w-4" />
              Transfer
            </Button>
            <Button onClick={openCreateForm}>
              <Plus className="mr-2 h-4 w-4" />
              Add Item
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Total Items"
            value={dashboard.metrics.totalItems.toLocaleString()}
            icon={<Package className="h-5 w-5" />}
          />
          <MetricCard
            label="Total Value"
            value={`$${dashboard.metrics.totalValue.toLocaleString()}`}
          />
          <MetricCard
            label="Low Stock Items"
            value={dashboard.stockDistribution.low + dashboard.stockDistribution.stockout}
            trend={{ direction: 'down', value: '', text: 'needs attention' }}
          />
          <MetricCard
            label="Active Alerts"
            value={dashboard.metrics.activeAlerts}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search inventory..."
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Optimal">Optimal</SelectItem>
            <SelectItem value="Low">Low Stock</SelectItem>
            <SelectItem value="Stockout">Out of Stock</SelectItem>
            <SelectItem value="Overstock">Overstock</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={items}
        columns={columns}
        loading={loading}
        onRowClick={(item) => navigate(`/inventory/${item.id}`)}
        emptyTitle="No inventory items found"
        emptyDescription="Get started by adding your first inventory item."
        emptyAction={
          <Button onClick={openCreateForm}>
            <Plus className="mr-2 h-4 w-4" />
            Add Item
          </Button>
        }
      />

      {/* Create/Edit Item Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingItem ? 'Edit Item' : 'Add Item'}</SheetTitle>
            <SheetDescription>
              {editingItem
                ? 'Update the inventory item details below.'
                : 'Fill in the details to create a new inventory item.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU *</Label>
                  <Input
                    id="sku"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                    placeholder="DRL-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Drill Bit 8.5in"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter item description..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subcategory">Subcategory</Label>
                  <Input
                    id="subcategory"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    placeholder="e.g., PDC Bits"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active Status</Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.isActive ? 'Item is active' : 'Item is inactive'}
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>

            {/* Stock Levels */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Stock Levels</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minStockLevel">Min Stock Level</Label>
                  <Input
                    id="minStockLevel"
                    type="number"
                    value={formData.minStockLevel}
                    onChange={(e) => setFormData({ ...formData, minStockLevel: e.target.value })}
                    placeholder="10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxStockLevel">Max Stock Level</Label>
                  <Input
                    id="maxStockLevel"
                    type="number"
                    value={formData.maxStockLevel}
                    onChange={(e) => setFormData({ ...formData, maxStockLevel: e.target.value })}
                    placeholder="100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="reorderPoint">Reorder Point</Label>
                  <Input
                    id="reorderPoint"
                    type="number"
                    value={formData.reorderPoint}
                    onChange={(e) => setFormData({ ...formData, reorderPoint: e.target.value })}
                    placeholder="20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="leadTimeDays">Lead Time (days)</Label>
                  <Input
                    id="leadTimeDays"
                    type="number"
                    value={formData.leadTimeDays}
                    onChange={(e) => setFormData({ ...formData, leadTimeDays: e.target.value })}
                    placeholder="7"
                  />
                </div>
              </div>
            </div>

            {/* Cost */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Cost</h4>
              <div className="space-y-2">
                <Label htmlFor="unitCost">Unit Cost ($)</Label>
                <Input
                  id="unitCost"
                  type="number"
                  step="0.01"
                  value={formData.unitCost}
                  onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingItem ? 'Update Item' : 'Create Item'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Deactivate Item"
        description={`Are you sure you want to deactivate "${deleteDialog.item?.name}"? This item will no longer be available for new operations.`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />

      {/* Restock Sheet */}
      <Sheet open={restockOpen} onOpenChange={setRestockOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Restock Item</SheetTitle>
            <SheetDescription>
              Request stock replenishment for {restockItem?.name}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Item Info */}
            {restockItem && (
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{restockItem.name}</p>
                  <p className="text-sm text-muted-foreground">{restockItem.sku}</p>
                </div>
              </div>
            )}

            <Separator />

            {/* Locations */}
            <div className="space-y-4">
              <h4 className="font-medium">Transfer Route</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Source Location *</Label>
                  <Select
                    value={restockData.sourceLocationId}
                    onValueChange={(value) => setRestockData({ ...restockData, sourceLocationId: value })}
                    disabled={loadingLocations}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination Location *</Label>
                  <Select
                    value={restockData.destinationLocationId}
                    onValueChange={(value) => setRestockData({ ...restockData, destinationLocationId: value })}
                    disabled={loadingLocations}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Quantity & Price */}
            <div className="space-y-4">
              <h4 className="font-medium">Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={restockData.quantity}
                    onChange={(e) => setRestockData({ ...restockData, quantity: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={restockData.unitPrice}
                    onChange={(e) => setRestockData({ ...restockData, unitPrice: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={restockData.notes}
                  onChange={(e) => setRestockData({ ...restockData, notes: e.target.value })}
                  placeholder="Add any notes for this restock request..."
                  rows={3}
                />
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRestockOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRestock} disabled={restockSaving}>
                {restockSaving ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Transfer Sheet */}
      <Sheet open={transferOpen} onOpenChange={setTransferOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Transfer Inventory</SheetTitle>
            <SheetDescription>
              Transfer multiple items between locations
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Locations */}
            <div className="space-y-4">
              <h4 className="font-medium">Transfer Route</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source Location *</Label>
                  <Select
                    value={transferData.sourceLocationId}
                    onValueChange={(value) => setTransferData({ ...transferData, sourceLocationId: value })}
                    disabled={loadingLocations}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination Location *</Label>
                  <Select
                    value={transferData.destinationLocationId}
                    onValueChange={(value) => setTransferData({ ...transferData, destinationLocationId: value })}
                    disabled={loadingLocations}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Items to Transfer *</h4>
                <Button type="button" variant="outline" size="sm" onClick={handleAddTransferItem}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Item
                </Button>
              </div>

              {transferData.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-6 text-center">
                  <Package className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No items added yet.</p>
                  <Button type="button" variant="outline" size="sm" className="mt-2" onClick={handleAddTransferItem}>
                    Add First Item
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {transferData.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-lg border p-3"
                    >
                      <div className="flex-1 space-y-3">
                        <div className="space-y-2">
                          <Label>Item</Label>
                          <Select
                            value={item.itemId}
                            onValueChange={(value) => handleTransferItemChange(index, 'itemId', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select item" />
                            </SelectTrigger>
                            <SelectContent>
                              {items.map((invItem) => (
                                <SelectItem key={invItem.id} value={invItem.id}>
                                  {invItem.name} ({invItem.sku})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleTransferItemChange(index, 'quantity', parseInt(e.target.value) || 1)
                            }
                          />
                        </div>
                        {item.item && (
                          <p className="text-sm text-muted-foreground">
                            Unit: {item.item.unit} | Available: {item.item.aggregated?.totalAvailable || 0}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() => handleRemoveTransferItem(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={transferData.notes}
                onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                placeholder="Add any notes for this transfer..."
                rows={3}
              />
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setTransferOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleTransfer} disabled={transferSaving}>
                {transferSaving ? 'Submitting...' : 'Submit Transfer'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Bulk Upload Sheet */}
      <Sheet open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Bulk Upload Inventory Items</SheetTitle>
            <SheetDescription>
              Import multiple inventory items from a CSV file. Download the template to see the required format.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            <BulkUpload
              columns={bulkUploadColumns}
              onUpload={handleBulkUpload}
              templateFileName="inventory_items_template.csv"
              maxRows={500}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
